import type { User } from '@shared/types'
import { logger } from '@shared/lib/utils/logger'

const USER_STORAGE_KEY = 'professionals_user'

/**
 * Centralized user data management in localStorage
 * Used for lazy authentication - stores user locally to avoid refetching on every page load
 * Backend validates auth for protected actions
 */
export const userStorage = {
  /**
   * Save user to localStorage
   */
  setUser(user: User): void {
    try {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
    } catch (error) {
      logger.error('Failed to save user to localStorage:', error)
    }
  },

  /**
   * Get user from localStorage
   */
  getUser(): User | null {
    try {
      const stored = localStorage.getItem(USER_STORAGE_KEY)
      return stored ? (JSON.parse(stored) as User) : null
    } catch (error) {
      logger.error('Failed to load user from localStorage:', error)
      return null
    }
  },

  /**
   * Remove user from localStorage
   */
  clearUser(): void {
    try {
      localStorage.removeItem(USER_STORAGE_KEY)
    } catch (error) {
      logger.error('Failed to clear user from localStorage:', error)
    }
  },

  /**
   * Check if user exists in localStorage
   */
  hasUser(): boolean {
    return !!this.getUser()
  },
}
